import { Plus, Trash2 } from "lucide-react";

/**
 * Inline editor for service variants (sub-options like "Honey Wax", "Rica Wax").
 *
 *   value: Array<{id?: string, name: string, price: number|string}>
 *   onChange: (next) => void
 *   testIdPrefix: string  (e.g., "new-service" or "edit-service")
 */
export default function VariantsEditor({
    value = [],
    onChange,
    testIdPrefix = "service",
    label = "Sub-options (optional)",
    addLabel = "Add option",
    emptyHint = 'None — service uses the base price. Add options (e.g. "Honey Wax", "Rica Wax") to let customers pick at booking time.',
    footnote = "When at least one option is added, customers MUST choose one at booking time. Variant price replaces base price.",
    namePlaceholder = "Option name",
}) {
    const variants = Array.isArray(value) ? value : [];

    const update = (idx, patch) => {
        onChange(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
    };
    const add = () => onChange([...variants, { name: "", price: "" }]);
    const remove = (idx) => onChange(variants.filter((_, i) => i !== idx));

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-variants`}>
            <div className="flex items-center justify-between">
                <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">{label}</span>
                <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-1 text-[0.6rem] tracking-[0.2em] uppercase text-[#8F622C] hover:text-[#3D0C1F] transition-colors"
                    data-testid={`${testIdPrefix}-variants-add`}
                >
                    <Plus size={11} /> {addLabel}
                </button>
            </div>
            {variants.length === 0 ? (
                <p className="text-[0.65rem] text-[#8A6574] italic">
                    {emptyHint}
                </p>
            ) : (
                <div className="space-y-2">
                    {variants.map((v, idx) => (
                        <div key={v.id || `new-${idx}`} className="flex items-center gap-2" data-testid={`${testIdPrefix}-variant-${idx}`}>
                            <input
                                required
                                placeholder={namePlaceholder}
                                value={v.name || ""}
                                onChange={(e) => update(idx, { name: e.target.value })}
                                className="flex-1 bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                                data-testid={`${testIdPrefix}-variant-${idx}-name`}
                            />
                            <input
                                required
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Price"
                                value={v.price === 0 ? 0 : (v.price || "")}
                                onChange={(e) => update(idx, { price: e.target.value })}
                                className="w-24 bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#8F622C] focus:border-[#C29A5B] outline-none"
                                data-testid={`${testIdPrefix}-variant-${idx}-price`}
                            />
                            <button
                                type="button"
                                onClick={() => remove(idx)}
                                className="text-[#7A5262] hover:text-red-700 p-2"
                                data-testid={`${testIdPrefix}-variant-${idx}-remove`}
                                aria-label="Remove option"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <p className="text-[0.6rem] text-[#8A6574] mt-1">{footnote}</p>
        </div>
    );
}
